// =============================================================================
// ZONE EDITOR 2D - UTILITIES
// =============================================================================

import type { Point2D, WorkspaceConfig } from '../../types'

/**
 * Converte coordinate del mondo (mm) in coordinate canvas (px)
 */
export function worldToCanvas(
  point: Point2D,
  workspace: WorkspaceConfig,
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  panOffset: Point2D
): Point2D {
  const scale = Math.min(canvasWidth / workspace.width, canvasHeight / workspace.height) * zoom
  const offsetX = (canvasWidth - workspace.width * scale) / 2 + panOffset.x
  const offsetY = (canvasHeight - workspace.height * scale) / 2 + panOffset.y

  return {
    x: point.x * scale + offsetX,
    y: canvasHeight - (point.y * scale + offsetY) // Inverti Y (0 in basso)
  }
}

/**
 * Converte coordinate canvas (px) in coordinate del mondo (mm)
 */
export function canvasToWorld(
  point: Point2D,
  workspace: WorkspaceConfig,
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  panOffset: Point2D
): Point2D {
  const scale = Math.min(canvasWidth / workspace.width, canvasHeight / workspace.height) * zoom
  const offsetX = (canvasWidth - workspace.width * scale) / 2 + panOffset.x
  const offsetY = (canvasHeight - workspace.height * scale) / 2 + panOffset.y

  return {
    x: (point.x - offsetX) / scale,
    y: (canvasHeight - point.y - offsetY) / scale // Inverti Y
  }
}

/**
 * Calcola la scala attuale (px per mm)
 */
export function getScale(
  workspace: WorkspaceConfig,
  canvasWidth: number,
  canvasHeight: number,
  zoom: number
): number {
  return Math.min(canvasWidth / workspace.width, canvasHeight / workspace.height) * zoom
}

/**
 * Verifica se un punto è dentro un poligono
 */
export function isPointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y
    const xj = polygon[j].x, yj = polygon[j].y

    if (((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  return inside
}

/**
 * Calcola il bounding box di un poligono
 */
export function getBoundingBox(points: Point2D[]): { min: Point2D; max: Point2D } {
  if (points.length === 0) {
    return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } }
  }

  let minX = points[0].x
  let minY = points[0].y
  let maxX = points[0].x
  let maxY = points[0].y

  for (const p of points) {
    minX = Math.min(minX, p.x)
    minY = Math.min(minY, p.y)
    maxX = Math.max(maxX, p.x)
    maxY = Math.max(maxY, p.y)
  }

  return {
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY }
  }
}

/**
 * Calcola il centro di un poligono
 */
export function getPolygonCenter(points: Point2D[]): Point2D {
  if (points.length === 0) return { x: 0, y: 0 }

  let sumX = 0
  let sumY = 0
  for (const p of points) {
    sumX += p.x
    sumY += p.y
  }

  return {
    x: sumX / points.length,
    y: sumY / points.length
  }
}

/**
 * Crea un rettangolo da due punti opposti
 */
export function createRectangle(p1: Point2D, p2: Point2D): Point2D[] {
  const minX = Math.min(p1.x, p2.x)
  const maxX = Math.max(p1.x, p2.x)
  const minY = Math.min(p1.y, p2.y)
  const maxY = Math.max(p1.y, p2.y)

  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY }
  ]
}

/**
 * Distanza tra due punti
 */
export function distance(p1: Point2D, p2: Point2D): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Formatta una distanza in mm per display
 */
export function formatDistance(mm: number): string {
  if (mm >= 1000) {
    return `${(mm / 1000).toFixed(1)}m`
  }
  return `${Math.round(mm)}mm`
}
